import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateSchoolInput,
  SchoolOutput,
  SchoolQuery,
  MutationCreateSchoolArgs,
  MutationUpdateSchoolArgs,
  MutationResendActivationLinkArgs,
  ResendActivationLinkInput,
  ClassCountBySchoolInput,
  ClassCountBySchoolOutput,
  LessonOutput,
  LessonTeacherSchoolQuery,
} from '../gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';
import {
  GET_VERIFIED_SCHOOLS,
  GET_SINGLE_SCHOOL,
  GET_CLASS_COUNT_BY_SCHOOL,
  GET_SCHOOL_LESSONS_WITH_PROGRESSIONS,
} from '../queries/establishment';
import {
  CREATE_SCHOOL,
  UPDATE_SCHOOL,
  RESEND_ACTIVATION_LINK,
} from '../mutations/establishment';

export const useGetVerifiedSchools = (query: SchoolQuery) => {
  return useQuery<SchoolOutput[], Error>({
    queryKey: ['verifiedSchools', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getVerifiedSchools: SchoolOutput[] },
        { query: SchoolQuery }
      >(GET_VERIFIED_SCHOOLS, { query });
      return data.getVerifiedSchools;
    },
    enabled: !!query,
  });
};

// Alias pour compatibilité
export const useGetSchools = useGetVerifiedSchools;

export const useGetSingleSchool = (getSchoolId: string) => {
  return useQuery<SchoolOutput, Error>({
    queryKey: ['school', getSchoolId],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSchool: SchoolOutput },
        { getSchoolId: string }
      >(GET_SINGLE_SCHOOL, { getSchoolId });
      return data.getSchool;
    },
    enabled: !!getSchoolId,
  });
};

export const useGetClassCountBySchool = (schoolId: string) => {
  return useQuery<ClassCountBySchoolOutput, Error>({
    queryKey: ['classCountBySchool', schoolId],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getClassCountBySchool: ClassCountBySchoolOutput },
        { query: ClassCountBySchoolInput }
      >(GET_CLASS_COUNT_BY_SCHOOL, { query: { schoolId } });
      return data.getClassCountBySchool;
    },
    enabled: !!schoolId,
  });
};

export const useGetSchoolLessonsWithProgressions = (schoolId: string) => {
  return useQuery<LessonOutput[], Error>({
    queryKey: ['schoolLessonsWithProgressions', schoolId],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getAllSchoolLessons: LessonOutput[] },
        { query: LessonTeacherSchoolQuery }
      >(GET_SCHOOL_LESSONS_WITH_PROGRESSIONS, { query: { school: schoolId } });
      return data.getAllSchoolLessons;
    },
    enabled: !!schoolId,
  });
};

export const useCreateSchool = () => {
  const queryClient = useQueryClient();
  return useMutation<SchoolOutput, Error, CreateSchoolInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createSchool: SchoolOutput;
        },
        MutationCreateSchoolArgs
      >(CREATE_SCHOOL, { input });
      return data.createSchool;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['schools'],
      });
      queryClient.invalidateQueries({
        queryKey: ['verifiedSchools'],
      });
    },
  });
};

export const useUpdateSchool = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { input: CreateSchoolInput; id: string }>({
    mutationFn: async ({ input, id }) => {
      const data = await graphqlFetch<
        {
          updateSchool: boolean;
        },
        MutationUpdateSchoolArgs
      >(UPDATE_SCHOOL, { id, input });
      return data.updateSchool;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schools'] });
      queryClient.invalidateQueries({ queryKey: ['verifiedSchools'] });
      queryClient.invalidateQueries({
        queryKey: ['school', variables.id],
      });
    },
  });
};

export const useResendActivationLink = () => {
  return useMutation<boolean, Error, ResendActivationLinkInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { resendActivationLink: boolean },
        MutationResendActivationLinkArgs
      >(RESEND_ACTIVATION_LINK, { input });
      return data.resendActivationLink;
    },
  });
};
